import { createSelector } from 'reselect'
import { getTourV2Items } from '../toursv2'
import { excludeNullOrUndefined } from 'checkout/utils'
import { getDepositServiceFeeConfig } from '../../featureConfig/deposit'
import { getFullOffers } from 'selectors/offerSelectors'
import getFlightBundledItemIds from '../getFlightBundledItemIds'
import { groupBy, nonNullable } from 'lib/array/arrayUtils'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { getTourV2ItemView } from 'checkout/lib/utils/tours/view'
import { getLabels } from 'checkout/lib/utils/accommodation/label'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import offerPageURL from 'lib/offer/offerPageURL'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getAccommodationBundledItemIds from 'checkout/selectors/view/getAccommodationBundledItemIds'

const getTourV2ItemViews = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getDepositServiceFeeConfig(state),
  (state: App.State) => getFullOffers(state),
  (state: App.State) => getFlightBundledItemIds(state),
  (state: App.State) => getAccommodationBundledItemIds(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (
    cartItems,
    serviceFee,
    offers,
    flightBundledItemIds,
    accommodationBundledItemIds,
    postPurchase,
    existingOrder,
  ): App.WithDataStatus<Array<App.Checkout.TourV2AccommodationOfferView>> => {
    const intl = getCurrentIntl()
    // Each item = 1 person, but we want to process the same departure/offer as a single view
    const itemsByBookingKey = groupBy(cartItems, item => `${item.offerId}-${item.purchasableOption.fkDepartureId}`)
    const itemGroups = itemsByBookingKey.values().map(items => ({
      departureId: items[0].purchasableOption.fkDepartureId,
      offerId: items[0].offerId,
      items,
    })).toArray()

    const views = itemGroups.map((itemGroup): App.WithDataStatus<App.Checkout.TourV2AccommodationOfferView | undefined> => {
      const { offerId, items } = itemGroup
      const offer = offers[offerId] as App.Tours.TourV2Offer
      if (!isTourV2Offer(offer)) {
        return {
          hasRequiredData: false,
          data: undefined,
        }
      }

      const firstItem = items[0]
      const { startDate, endDate, duration, image, purchasableOption } = firstItem

      const itemViewsWithStatuses = items.map(
        (item, index) => getTourV2ItemView(getCurrentIntl(), item, index, offer, postPurchase, existingOrder),
      )

      const variationId = offer?.variations[purchasableOption.fkVariationId] ?
        purchasableOption.fkVariationId :
        Object.values(offer?.variations ?? {}).find(variation => variation.fkTourOptionId === purchasableOption.fkVariationId)?.id

      const bundledItemIds = new Set([...flightBundledItemIds, ...accommodationBundledItemIds])

      const offerView: App.Checkout.TourV2AccommodationOfferView = {
        offerId,
        offerPageUrl: offerPageURL(offer),
        ...getLabels(offer, variationId),
        bedGroups: [],
        confidenceLabels: [],
        duration,
        startDate,
        endDate,
        image,
        location: getLocationString(getCurrentIntl(), offer, variationId),
        occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
        offerType: 'tour_v2',
        reservationType: 'instant_booking',
        urgencyLabels: [],
        saleUnit: 'room',
        durationLabel: intl.formatMessage(
          {
            id: 'DzOQT2DMDM',
            defaultMessage: '{count, plural, one {# day} other {# days}}',
            description: 'Tour duration label with pluralization on the tour offer detail page',
          },
          { count: duration },
        ),
        offerLoaded: true,
        itemViews: itemViewsWithStatuses.map(i => i.data),
        propertyTimezone: 'GMT',
        operatorName: offer ? intl.formatMessage(
          {
            id: 'iEiRM4lSHH',
            defaultMessage: 'Operated by: {operatorName}',
            description: 'Label displaying the tour operator name on the tour offer detail overview panel',
          },
          { operatorName: offer.operatedBy },
        ) : '',
        offer,
        designation: intl.formatMessage({
          id: 'crX+fkIBYz',
          defaultMessage: 'Tour',
          description: 'Item type designation label for a tour booking in the order summary display',
        }),
        depositType: offer?.depositType,
        depositAmount: offer?.depositAmount ?? undefined,
        serviceFee: isSalesforceTourV2(offer) ? serviceFee : undefined,
        isBundled: items.some(item => bundledItemIds.has(item.itemId)),
      }

      return {
        data: offerView,
        hasRequiredData: itemViewsWithStatuses.every(view => view.hasRequiredData),
      }
    })

    return {
      data: nonNullable(views.map(view => view.data)),
      hasRequiredData: views.every(view => view.hasRequiredData),
    }
  })

export default getTourV2ItemViews
